import React from 'react';
import toast from 'react-hot-toast';
import ShareTextModal from '../../common/ShareTextModal';
import { generateDownloadFilename } from '../../../utils/fileUtils';
import { API_BASE_URL } from '../../../lib/constants';

const ResultSection = ({ t, generatedImage, toolIcon, handleDownload, toolName, gradientColors }) => {
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareMediaUrl, setShareMediaUrl] = React.useState('');
  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  const isMulti = Array.isArray(generatedImage);
  const hasResult = isMulti ? generatedImage && generatedImage.length > 0 : !!generatedImage;

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE_URL}${url}`;
  };

  const getImageUrl = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return getAbsoluteUrl(image);
    const url = image.url || image.imageUrl || '';
    return getAbsoluteUrl(url);
  };

  const getImageId = (image, index) => {
    if (!image) return `image-${index + 1}`;
    return image.id || image.generationId || image.jobId || `image-${index + 1}`;
  };

  const getImageExt = (url, fallback = 'png') => {
    if (!url) return fallback;
    const match = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
    if (!match || !match[1]) return fallback;
    const ext = match[1].toLowerCase();
    if (ext === 'jpeg') return 'jpg';
    if (['jpg', 'png', 'webp', 'gif'].includes(ext)) return ext;
    return fallback;
  };

  const handleSinglePreview = () => {
    const url = getImageUrl(generatedImage);
    if (url) window.open(url, '_blank');
  };

  const handleCardPreview = (image) => {
    const url = getImageUrl(image);
    if (url) window.open(url, '_blank');
  };

  const handleCardDownload = async (image, index) => {
    const url = getImageUrl(image);
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to download image');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const ext = getImageExt(url);
      const id = getImageId(image, index);
      const filename = generateDownloadFilename(id);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${filename}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      toast.success(t('showcase.downloadStarted'));
    } catch (e) {
      console.error('Download failed:', e);
      toast.error(t('aiToolTemplate.downloadFailed'));
    }
  };

  const handleMainDownload = () => {
    if (!hasResult) return;
    if (isMulti) {
      try {
        const images = generatedImage.filter((img) => img && getImageUrl(img));
        images.forEach((image, index) => {
          handleCardDownload(image, index);
        });
      } catch (e) {
        console.error('Download all failed:', e);
        toast.error(t('aiToolTemplate.downloadFailed'));
      }
    } else if (typeof handleDownload === 'function') {
      handleDownload();
    }
  };

  return (
    <div className="result-section">
      <div className="section-header">
        <h2>{t('aiToolTemplate.generationResult')}</h2>
        <p>{hasResult ? t('aiToolTemplate.resultCompleted', { toolName }) : t('aiToolTemplate.resultWaiting')}</p>
      </div>

      {hasResult ? (
        <>
          {!isMulti && (
            <div className="result-image" onClick={handleSinglePreview} style={{ position: 'relative' }}>
              <img src={getImageUrl(generatedImage)} alt={`Generated ${toolName}`} style={{ objectPosition: 'top center', cursor: 'zoom-in' }} />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const share = generatedImage?.shareUrl || '';
                  if (!share) return;
                  setShareMediaUrl(share);
                  setShareOpen(true);
                }}
                disabled={!generatedImage?.shareUrl}
                style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 8, border: '1px solid #444', background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: generatedImage?.shareUrl ? 'pointer' : 'not-allowed', opacity: generatedImage?.shareUrl ? 1 : 0.6 }}
                aria-label={t('creationRecord.share')}
              >
                <img src="/icons/share.svg" alt={t('creationRecord.share')} style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)', padding: 0 }} />
              </button>
            </div>
          )}

          {isMulti && (
            <div
              className="result-image-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}
            >
              {generatedImage.map((image, index) => {
                const url = getImageUrl(image);
                if (!url) return null;
                const id = getImageId(image, index);
                const isHovered = hoveredIndex === index;
                return (
                  <div
                    key={id}
                    className="result-image-card"
                    onClick={() => handleCardPreview(image)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    style={{
                      position: 'relative',
                      borderRadius: 12,
                      overflow: 'hidden',
                      background: 'var(--color-bg-card, #1e1e1e)',
                      border: '1px solid var(--color-border, #333333)',
                      cursor: 'zoom-in'
                    }}
                  >
                    {isHovered && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const share = image?.shareUrl || '';
                          if (!share) return;
                          setShareMediaUrl(share);
                          setShareOpen(true);
                        }}
                        disabled={!image?.shareUrl}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: '1px solid #444',
                          background: 'rgba(0,0,0,0.45)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: image?.shareUrl ? 'pointer' : 'not-allowed',
                          opacity: image?.shareUrl ? 1 : 0.6,
                          zIndex: 1
                        }}
                        aria-label={t('creationRecord.share')}
                      >
                        <img
                          src="/icons/share.svg"
                          alt={t('creationRecord.share')}
                          style={{ width: 14, height: 14, filter: 'brightness(0) invert(1)', padding: 0 }}
                        />
                      </button>
                    )}
                    <img
                      src={url}
                      alt={`Generated ${toolName} ${index + 1}`}
                      style={{
                        width: '100%',
                        height: 180,
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    {isHovered && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardDownload(image, index);
                        }}
                        aria-label={t('aiToolTemplate.downloadImage')}
                        style={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          border: '1px solid #444',
                          background: 'rgba(0,0,0,0.45)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 10px 26px rgba(59,130,246,0.28)',
                          cursor: 'pointer'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3v10" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                          <path d="M7 10l5 5 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M5 21h14" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="result-actions">
            <button
              className="download-button"
              onClick={handleMainDownload}
              style={{ background: gradientColors?.[0] }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {isMulti ? t('aiToolTemplate.downloadAllImages') : t('aiToolTemplate.downloadImage')}
            </button>
          </div>
          <ShareTextModal
            open={shareOpen}
            title={t('creationRecord.enterShareText')}
            placeholder={t('creationRecord.enterShareText')}
            maxLength={2000}
            platform={null}
            mediaUrl={shareMediaUrl}
            mediaType="image"
            by=""
            onConfirm={() => {
              setShareOpen(false);
            }}
            onCancel={() => {
              setShareOpen(false);
            }}
          />
        </>
      ) : (
        <div className="result-placeholder">
          <div className="placeholder-icon">{toolIcon}</div>
          <h3>{t('aiToolTemplate.waitingGeneration')}</h3>
          <p>{t('aiToolTemplate.waitingDescription', { toolName })}</p>
        </div>
      )}
    </div>
  );
};

export default ResultSection;
